import {
  useCreateDefenseBridgeAsync,
  useGetDefenseBridgeAsync,
  useUpdateDefenseBridgeAsync,
} from '../../../services/Defense/Bridge/hooks/useMutations';
import React, { useContext, useEffect } from 'react';
import zod from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DefenseBridgeConfigSchema } from '../../../services/Defense/Bridge/schemas';
import { AppContext } from '../../../context/AppContextProvider';
import {
  Box,
  Grid2,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import TextFieldComponent from '../../../components/TextFieldComponent';
import TooltipComponent from '../../../components/TooltipComponent';
import SwitchComponent from '../../../components/SwitchComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { ExpandMore } from '@mui/icons-material';
import { t } from 'i18next';

interface DefenseBridgeEditionFormProps {
  defenseBridgeIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type DefenseBridgeEditionFormInput = zod.infer<
  typeof DefenseBridgeConfigSchema
>;

const DefenseBridgeEditionForm = ({
  defenseBridgeIdToUpdate,
  handleSaveForm,
}: DefenseBridgeEditionFormProps) => {
  const { control, reset, handleSubmit } =
    useForm<DefenseBridgeEditionFormInput>({
      resolver: zodResolver(DefenseBridgeConfigSchema),
      defaultValues: {
        name: '',
        description: '',
        isActive: true,
        serverIp: '',
        serverPort: 80,
        accessKey: '',
        secretKey: '',
      },
    });
  const { mutateAsync: mutateGet } = useGetDefenseBridgeAsync();
  const { mutateAsync: mutateCreate } = useCreateDefenseBridgeAsync();
  const { mutateAsync: mutateUpdate } = useUpdateDefenseBridgeAsync();
  const { showSnackbar } = useContext(AppContext);

  const handleSave = async (data: DefenseBridgeEditionFormInput) => {
    if (defenseBridgeIdToUpdate) {
      await mutateUpdate(
        { id: defenseBridgeIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration('Integração defense atualizada com sucesso.');
          },
        },
      );
      return;
    }
    await mutateCreate(data, {
      onSuccess: () => {
        saveIntegration('Integração defense criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (defenseBridgeIdToUpdate) {
      mutateGet(defenseBridgeIdToUpdate).then((i) =>
        reset(i, { keepDefaultValues: true }),
      );
    }
  }, [defenseBridgeIdToUpdate]);

  return (
    <Grid2 container size={12} spacing={2}>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label={t('integrationsPage.defenseBridgeForms.name')}
              onChangeText={(val) => onChange(val)}
              type={'text'}
              errorMessage={error?.message}
              value={value}
            />
          )}
          name={'name'}
          control={control}
        />
      </Grid2>

      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label={t('integrationsPage.defenseBridgeForms.ipServer')}
              onChangeText={(val) => onChange(val)}
              type={'text'}
              errorMessage={error?.message}
              value={value}
              mask={'000.000.000.000'}
              maxLength={15}
            />
          )}
          name={'serverIp'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label={t('integrationsPage.defenseBridgeForms.doorServer')}
              onChangeText={(val) => onChange(Number(val))}
              type={'text'}
              errorMessage={error?.message}
              value={value}
            />
          )}
          name={'serverPort'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label={t('integrationsPage.defenseBridgeForms.description')}
              onChangeText={(val) => onChange(val)}
              type={'text'}
              errorMessage={error?.message}
              value={value}
              multiline
              maxRows={4}
            />
          )}
          name={'description'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label={t('integrationsPage.defenseBridgeForms.accessKey')}
              onChangeText={(val) => onChange(val)}
              type={'text'}
              errorMessage={error?.message}
              value={value}
            />
          )}
          name={'accessKey'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label={t('integrationsPage.defenseBridgeForms.secretKey')}
              onChangeText={(val) => onChange(val)}
              type={'text'}
              errorMessage={error?.message}
              value={value}
            />
          )}
          name={'secretKey'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Box width={'14%'}>
              <TooltipComponent
                title={value ? 'Ativo' : 'Inativo'}
                placement={'right'}
              >
                <SwitchComponent
                  label={t('integrationsPage.defenseBridgeForms.status')}
                  value={value}
                  onChange={(value) => onChange(value)}
                  color={'primary'}
                />
              </TooltipComponent>
            </Box>
          )}
          name={'isActive'}
          control={control}
        />
      </Grid2>
      <Grid2 size={12} sx={{ mt: 2 }}>
        <ButtonComponent
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit(handleSave)}
        >
          {t('integrationsPage.defenseBridgeForms.save')}
        </ButtonComponent>
      </Grid2>
    </Grid2>
  );
};

export default DefenseBridgeEditionForm;
